/**
 * buildCheckableItems - Builds the list of checkable field keys for transfer checklists.
 *
 * Shared logic used by all entity change cards to compute which fields need
 * transfer checkboxes when the entity is UPDATED.
 */

import type { ChangeType, DomainEventSummarySchema } from '@/services/api/generated/crossMatterChangesService';

import type { EntityChanges } from './useEntityChanges';

export interface CheckableFieldConfig {
  /** The field key or array key in the entity data */
  key: string;
  /**
   * - 'field': simple field, checked via isFieldChanged
   * - 'nested': object field, checked via hasChangesUnder OR isFieldChanged
   * - 'array': array field, each item checked via hasChangesUnder per index
   */
  type: 'field' | 'nested' | 'array';
}

/**
 * Builds the list of checkable item keys for transfer checklists.
 *
 * For UPDATED entities: returns keys of changed fields/items.
 * For UPDATED entities with no diffs: returns event code keys.
 * For CREATED/DELETED/SAME: returns empty array (handled by acknowledge checkbox).
 */
export function buildCheckableItems(
  changeType: ChangeType,
  entityData: Record<string, unknown> | undefined,
  events: DomainEventSummarySchema[],
  changes: EntityChanges,
  fieldConfigs: CheckableFieldConfig[],
): string[] {
  if (changeType !== 'UPDATED') return [];
  if (!entityData) return [];

  // No field-level diffs — fall back to event codes
  if (changes.totalChanges === 0) {
    const codes = [...new Set(events.map((e) => e.event_code))];
    return codes.map((c) => `event.${c}`);
  }

  return fieldConfigs.flatMap((config) => {
    if (config.type === 'field') {
      return changes.isFieldChanged(config.key) ? [config.key] : [];
    }
    if (config.type === 'nested') {
      return changes.hasChangesUnder(config.key) ||
        changes.isFieldChanged(config.key)
        ? [config.key]
        : [];
    }
    // array
    const arr = (entityData[config.key] ?? []) as unknown[];
    return arr.reduce<string[]>((acc, _, idx) => {
      if (changes.hasChangesUnder(`${config.key}.${idx}`)) {
        acc.push(`${config.key}.${idx}`);
      }
      return acc;
    }, []);
  });
}
